import React, { createContext, useState, useCallback, useContext, useRef } from 'react';
import Authenticator from 'components/mfa/Authenticator';
import { apiPost } from 'utils/apiFetch';
import apiPath from 'utils/apiPath';

/**
 * MFAContext
 *
 * This is the reusable "MFA is required" entry point the spec asked for.
 * Mount <MFAProvider> once near the root (see App.js notes below) and any
 * component, anywhere, can do:
 *
 *   const { requireMfa } = useMFA();
 *   const ok = await requireMfa();
 *   if (!ok) return; // user cancelled or failed verification
 *   // ...continue the sensitive action, e.g. include result.stepUpProof
 *   //    in the actual protected API call so the backend can check it...
 *
 * IMPORTANT (per the security requirement): the `true`/`false` this hook
 * resolves with is ONLY for controlling frontend flow (whether to show a
 * spinner, proceed to the next step, etc). It is NOT itself proof of
 * anything to the backend. The real proof is `result.stepUpProof` — a
 * short-lived, single-use, server-issued token from POST /v1/mfa/verify.
 * Any future sensitive endpoint (payroll, bank details, role changes, ...)
 * must require that token and validate it server-side via
 * mfaProofService.consumeStepUpProof() — never trust the boolean alone.
 *
 * This context intentionally only covers the ALREADY-AUTHENTICATED case
 * (calls verifyToken-protected /v1/mfa/verify). Login-time MFA is a
 * separate, unauthenticated flow — see pages/Login.mfa-integration.snippet.jsx.
 */

const MFAContext = createContext(null);

export const MFAProvider = ({ children }) => {
  const [modalState, setModalState] = useState({ isOpen: false, options: {} });
  const resolverRef = useRef(null);

  const requireMfa = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalState({ isOpen: true, options });
    });
  }, []);

  const closeModal = useCallback((result) => {
    setModalState({ isOpen: false, options: {} });
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  const handleVerify = async (code, { isRecoveryCode }) => {
    const endpoint = isRecoveryCode ? apiPath.mfaVerifyRecoveryCode : apiPath.mfaVerify;
    const body = isRecoveryCode
      ? { code, context: modalState.options.context }
      : { token: code, context: modalState.options.context };
    const { data } = await apiPost(endpoint, body);
    // data: { success, verified, data: { stepUpProof }, message }
    return {
      verified: !!data?.verified,
      message: data?.message,
      stepUpProof: data?.data?.stepUpProof,
    };
  };

  return (
    <MFAContext.Provider value={{ requireMfa }}>
      {children}
      <Authenticator
        isOpen={modalState.isOpen}
        onClose={() => closeModal(false)}
        onVerify={handleVerify}
        onSuccess={(result) => closeModal({ verified: true, stepUpProof: result.stepUpProof })}
        allowRecoveryCode
        title={modalState.options.title}
        description={modalState.options.description}
      />
    </MFAContext.Provider>
  );
};

/**
 * useMFA().requireMfa() resolves to either:
 *   false                                -> cancelled/failed, do not proceed
 *   { verified: true, stepUpProof }      -> proceed; send stepUpProof to
 *                                            the protected backend endpoint
 */
export const useMFA = () => {
  const ctx = useContext(MFAContext);
  if (!ctx) throw new Error('useMFA() must be used within <MFAProvider>.');
  return ctx;
};

export default MFAContext;
